import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { ICreateManualQuotation, ILogisticServiceWithProductPrices } from 'types/services';
import LSEditionHeader from './components/LSEditionHeader/LSEditionHeader';
import ToolEditionCard from './components/ToolEditionCard/ToolEditionCard';
import { ILogisticServiceEditionForm } from './LogisticServiceEdition.types';
import { NudosButton } from 'components/NudosComponents';
import LSEditionTotals from './components/LSEditionTotals/LSEditionTotals';
import { FREE_ZONE_SPECIAL_BUSINESS_NAME_TYPES } from 'utils/productDefinitions';
import { createManualQuotation } from 'services/priceEdition/logisticServicesPriceEdition.service';
import CommentsModal from './components/CommentsModal/CommentsModal';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import {
  buildPriceEditionPayload,
  EDITION_FORM_DEFAULT_VALUES,
  initializePriceEditionForm
} from 'utils/logisticServiceEdition';
import './LogisticServiceEditionModule.scss';

interface ILogisticServiceEditionModuleProps {
  LSData?: ILogisticServiceWithProductPrices;
}

const LogisticServiceEditionModule = ({ LSData }: ILogisticServiceEditionModuleProps) => {
  const methods = useForm<ILogisticServiceEditionForm>({
    defaultValues: EDITION_FORM_DEFAULT_VALUES
  });
  const { control, handleSubmit, reset } = methods;
  const { fields: products } = useFieldArray({
    control,
    name: 'products'
  });
  const history = useHistory();

  const [hasTaxes, setHasTaxes] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const onSubmit = async (data: ILogisticServiceEditionForm) => {
    setIsSaving(true);
    const dataCopy: ILogisticServiceEditionForm = JSON.parse(JSON.stringify(data)); // copia profunda del objeto data para evitar mutaciones y no perder datos
    dataCopy.products = data.products?.map(product => {
      return {
        ...product,
        logisticServiceProductPrices:
          product.logisticServiceProductPrices?.filter(service => service.logisticServiceTypeId) || []
      };
    });
    reset(dataCopy);
    const payload: ICreateManualQuotation = buildPriceEditionPayload(dataCopy, LSData);
    try {
      await createManualQuotation(payload);
      history.push(`/logistic-service/details/${LSData?.id}`, { initialTab: 3 });
    } catch (error) {
      displayErrorNotification({
        customJSXMessage: (
          <>No se pudieron guardar los cambios, intenta de nuevo o comunícate con soporte para obtener ayuda.</>
        )
      });
    } finally {
      setShowCommentsModal(false);
      setIsSaving(false);
    }
  };

  const initializeFormValues = () => {
    reset(initializePriceEditionForm(LSData));
    // no puede existir el specialBusinessNameTypeId 0
    const isFreeZone =
      FREE_ZONE_SPECIAL_BUSINESS_NAME_TYPES.includes(LSData?.billingData?.specialBusinessNameTypeId || 0) &&
      LSData?.destinationProductLocation?.country === LSData?.billingData?.countryCode;
    setHasTaxes(isFreeZone);
  };

  useEffect(() => {
    initializeFormValues();
  }, [LSData]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FormProvider {...methods}>
      <form className="editService" onSubmit={handleSubmit(onSubmit)}>
        {/* Nota: Cuando el modal está abierto, el botón de envío está en CommentsModal. Cuando el modal está cerrado, el botón de envío está en el formulario. */}
        {showCommentsModal && (
          <CommentsModal handleCloseModal={() => setShowCommentsModal(false)} isSaving={isSaving} />
        )}
        <main className="editionContent">
          <LSEditionHeader
            companyName={LSData?.billingData?.name || undefined}
            logisticServiceId={LSData?.id || undefined}
            exchangeRate={Number(LSData?.logisticServicePrice?.exchangeRate) || undefined}
            currency={LSData?.logisticServicePrice?.currency || undefined}
          />
          <h2 className="editLSTitle">Resumen del servicio</h2>
          <section className="cardsContainer">
            {products.length > 0 &&
              products.map((product, index) => (
                <div className="toolCardContainer" key={product.product?.sku || ''}>
                  {' '}
                  <ToolEditionCard product={product} productIndex={index} />
                  {index + 1 < products.length && <hr />}
                </div>
              ))}
          </section>
          <hr className="divider" />
          <LSEditionTotals
            logisticServiceData={LSData}
            currency={LSData?.logisticServicePrice?.currency || 'USD'}
            hasTaxes={hasTaxes}
            hasCommission={LSData?.paymentInfo?.paymentTypeInfo?.name?.toLowerCase().includes('tarjeta') || false}
          />
        </main>
        <footer className="formFooter">
          <NudosButton
            buttonText="Cancelar y volver"
            colorPalette="secondary"
            handleClick={() => (LSData?.id ? history.push(`/logistic-service/details/${LSData.id}`) : history.goBack())}
          />
          <NudosButton
            buttonText="Guardar cambios"
            colorPalette="primary"
            type={!LSData?.quoteDocumentUrl ? 'submit' : 'button'}
            handleClick={LSData?.quoteDocumentUrl ? () => setShowCommentsModal(true) : undefined}
            isButtonLoading={isSaving}
          />
        </footer>
      </form>
    </FormProvider>
  );
};

export default LogisticServiceEditionModule;
